import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Clock, ArrowRight, ShieldCheck, Receipt, CreditCard } from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import { API_BASE_URL } from '../../utils/api';
import './MembershipSuccess.css';

const MembershipSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, talent: authTalent } = useTalentAuth();
  const [talentProfile, setTalentProfile] = useState<any>(null);

  // Extract IPG transaction params if available
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get('orderId');
  const approvalCode = searchParams.get('approvalCode');
  const amount = searchParams.get('amount');
  const cardBrand = searchParams.get('cardBrand');
  const txnDate = searchParams.get('date');

  const fetchTalentProfile = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/talent/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.talent) {
        setTalentProfile(data.talent);
      }
    } catch (err) {
      console.error('Error fetching talent profile in MembershipSuccess:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      // Auto-confirm IPG order if orderId is in URL
      if (orderId) {
        fetch(`${API_BASE_URL}/membership/confirm-ipg-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            orderId,
            approvalCode,
            amount,
            cardBrand,
            date: txnDate
          })
        }).then(() => fetchTalentProfile()).catch(() => fetchTalentProfile());
      } else {
        fetchTalentProfile();
      }
    }
  }, [token, orderId, approvalCode, amount, cardBrand, txnDate, fetchTalentProfile]);

  const activeProfile = talentProfile || authTalent;
  
  const isDetailedInfoComplete = Boolean(
    activeProfile && (
      (activeProfile.bio && String(activeProfile.bio).trim().length > 0) ||
      (activeProfile.phone && String(activeProfile.phone).trim().length > 0) ||
      (activeProfile.address && String(activeProfile.address).trim().length > 0)
    )
  );

  const isSkillsComplete = Boolean(
    activeProfile && (
      (Array.isArray(activeProfile.skills) && activeProfile.skills.length > 0) ||
      (activeProfile.experience_years && Number(activeProfile.experience_years) > 0) ||
      (activeProfile.yearsOfExperience && Number(activeProfile.yearsOfExperience) > 0)
    )
  );

  const isVerificationComplete = Boolean(
    activeProfile && (
      activeProfile.verification_status === 'verified' ||
      activeProfile.verificationStatus === 'verified'
    )
  );

  const isSearchVisibilityComplete = Boolean(
    activeProfile && (
      activeProfile.profile_status === 'active' ||
      activeProfile.profileStatus === 'active' ||
      activeProfile.status === 'active'
    )
  );

  const tradeName = searchParams.get('tradeName') || 'SkillSeek Limited';
  const currency = searchParams.get('currency') || 'USD';
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  const handleResendReceipt = async () => {
    if (!token || !orderId) return;
    setResendingEmail(true);
    setEmailStatus(null);
    try {
      const response = await fetch(`${API_BASE_URL}/membership/resend-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ orderId })
      });
      const data = await response.json();
      if (data.success) {
        setEmailStatus(`Receipt email successfully sent to ${data.email || 'your email'}`);
      } else {
        setEmailStatus(data.message || 'Failed to resend receipt');
      }
    } catch (err: any) {
      setEmailStatus('Could not resend email. Please try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="membership-success-container" style={{ padding: '40px 20px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      <div className="success-content" style={{ maxWidth: '600px', width: '100%', backgroundColor: '#ffffff', borderRadius: '16px', padding: '36px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', border: '1px solid #e2e8f0', textAlign: 'center' }}>
        <div className="success-icon" style={{ color: '#10b981', display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <ShieldCheck size={64} className="text-success" />
        </div>
        
        <h2 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '8px', fontSize: '26px' }}>Payment Confirmed!</h2>
        <p className="success-message" style={{ color: '#475569', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
          Welcome to your new membership! Your payment was successfully processed by Fiserv IPG. An official receipt has been emailed to you.
        </p>

        {/* IPG Transaction Details Card covering all 7 Fiserv fields */}
        {orderId && (
          <div
            style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              textAlign: 'left'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0A2E7C', fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Receipt size={16} />
                <span>Payment Receipt Details</span>
              </div>
              <button
                type="button"
                onClick={handleResendReceipt}
                disabled={resendingEmail}
                style={{
                  background: 'transparent',
                  border: '1px solid #0284c7',
                  color: '#0284c7',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: resendingEmail ? 'not-allowed' : 'pointer',
                  opacity: resendingEmail ? 0.6 : 1
                }}
              >
                {resendingEmail ? 'Sending...' : 'Resend Receipt Email'}
              </button>
            </div>

            {emailStatus && (
              <div style={{ fontSize: '12px', color: emailStatus.includes('success') ? '#16a34a' : '#dc2626', marginBottom: '10px', fontWeight: 500 }}>
                {emailStatus}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Company Trade Name:</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{tradeName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Processing Date:</span>
                <span style={{ fontWeight: 500, color: '#475569' }}>{txnDate || new Date().toUTCString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Order Number:</span>
                <span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>{orderId}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Card Type Used:</span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>{cardBrand || 'Credit Card'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Transaction Amount:</span>
                <span style={{ fontWeight: 800, color: '#0284c7' }}>${amount ? parseFloat(amount).toFixed(2) : '10.00'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Currency Type:</span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>{currency}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Authorization / Approval Code:</span>
                <span style={{ fontWeight: 600, color: '#16a34a', fontFamily: 'monospace' }}>{approvalCode || 'APPROVED'}</span>
              </div>
            </div>
          </div>
        )}
        
        <div className="success-features" style={{ textAlign: 'left', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', marginBottom: '28px' }}>
          <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '14px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Complete your profile to:
          </h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#334155' }}>
              {isDetailedInfoComplete ? (
                <CheckCircle size={16} className="text-success" style={{ color: '#10b981', flexShrink: 0 }} />
              ) : (
                <Clock size={16} className="text-muted" style={{ color: '#94a3b8', flexShrink: 0 }} />
              )}
              <span>Attract more customers with detailed information</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#334155' }}>
              {isSkillsComplete ? (
                <CheckCircle size={16} className="text-success" style={{ color: '#10b981', flexShrink: 0 }} />
              ) : (
                <Clock size={16} className="text-muted" style={{ color: '#94a3b8', flexShrink: 0 }} />
              )}
              <span>Showcase your skills and experience</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#334155' }}>
              {isVerificationComplete ? (
                <CheckCircle size={16} className="text-success" style={{ color: '#10b981', flexShrink: 0 }} />
              ) : (
                <Clock size={16} className="text-muted" style={{ color: '#94a3b8', flexShrink: 0 }} />
              )}
              <span>Build trust with profile verification</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#334155' }}>
              {isSearchVisibilityComplete ? (
                <CheckCircle size={16} className="text-success" style={{ color: '#10b981', flexShrink: 0 }} />
              ) : (
                <Clock size={16} className="text-muted" style={{ color: '#94a3b8', flexShrink: 0 }} />
              )}
              <span>Increase your visibility in search results</span>
            </li>
          </ul>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button 
            className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
            onClick={() => navigate('/talent/profile')}
            style={{ padding: '12px 24px', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #185FA5 0%, #0d386b 100%)', border: 'none', color: '#ffffff' }}
          >
            Complete My Profile
            <ArrowRight size={18} />
          </button>
          <button 
            className="btn skip-button"
            onClick={() => navigate('/talent/dashboard')}
            style={{ padding: '10px 24px', fontWeight: 600, borderRadius: '8px' }}
          >
            Skip for Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default MembershipSuccess;

